import type { FormConfiguration, FieldDefinition } from '@store/models/booking';
import { getVisibleFields } from '@utils/formValidation';

export function getStepFieldKeys(config: FormConfiguration, stepIndex: number): Set<string> {
  const step = config.steps[stepIndex];
  if (!step) return new Set();

  const keys = new Set<string>();

  if (step.fields) {
    for (const field of step.fields) {
      keys.add(field.key);
    }
  }

  if (step.sections) {
    for (const section of step.sections) {
      for (const field of section.fields) {
        keys.add(field.key);
      }
    }
  }

  return keys;
}

export function getAllStepFields(config: FormConfiguration, stepIndex: number): FieldDefinition[] {
  const step = config.steps[stepIndex];
  if (!step) return [];

  const fields: FieldDefinition[] = [];

  if (step.fields) {
    fields.push(...step.fields);
  }

  if (step.sections) {
    for (const section of step.sections) {
      fields.push(...section.fields);
    }
  }

  return fields;
}

export function filterFormDataByStep(
  formData: Record<string, unknown>,
  fieldKeys: Set<string>,
  stepFields: FieldDefinition[],
  currentFormData: Record<string, unknown>
): Record<string, unknown> {
  const visibleFields = getVisibleFields(stepFields, currentFormData);
  const visibleKeys = new Set(visibleFields.map((f) => f.key));

  // Collect prefixes from dotted field keys (e.g., "sponsor" from "sponsor.name")
  // so that companion keys like "sponsor.email" are also included
  const prefixes = new Set<string>();
  for (const key of fieldKeys) {
    const dotIndex = key.indexOf('.');
    if (dotIndex > 0) {
      prefixes.add(key.slice(0, dotIndex + 1));
    }
  }

  const filtered: Record<string, unknown> = {};
  for (const key of fieldKeys) {
    if (visibleKeys.has(key) && key in formData) {
      filtered[key] = formData[key];
    }
  }

  // Include companion dotted keys from formData that share the same prefix
  for (const [key, value] of Object.entries(formData)) {
    if (key in filtered) continue;
    for (const prefix of prefixes) {
      if (key.startsWith(prefix)) {
        filtered[key] = value;
        break;
      }
    }
  }

  return filtered;
}
